import React from "react";
import { createRoot } from "react-dom/client";
import {
  HashRouter as Router,
  Route,
  Routes,
  Navigate,
} from "react-router-dom";
import "primereact/resources/themes/mdc-light-deeppurple/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "primeflex/primeflex.css";
import "./assets/css/custom.css";
import "style.css";
import axios from "axios";

import Home from "views/home";
import Gallery from "views/gallery";
import Blinds from "views/blinds";
import Cart from "views/cart";
import Item from "views/Item";
import ScrollToTop from "./components/ScrollToTop";
import { Provider } from "react-redux";
import { store, persistor } from "./store";
import { PersistGate } from "redux-persist/integration/react";

import Footer from "components/footer";
import Header from "components/header";
import ContactUs from "views/contact-us";
import AdminCart from "views/admin-cart";
import Items from "views/items";
import Calculator from "views/calculator";
import Login from "views/login";
import { LanguageProvider } from "./language-maps/language-context";
import OrderStatus from "./views/order-status";
import SnowOverlay from "./components/snow-overlay";

const container = document.getElementById("root");
const root = createRoot(container);

// A stored JWT counts as "logged in" only if it is well-formed AND not past its
// `exp`. Previously the router trusted mere presence, so an expired 24h session
// still opened admin pages and then failed on save with a confusing 401.
const isAdminAuthenticated = () => {
  const token = localStorage.getItem("token");
  if (!token) return false;
  try {
    const part = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(part));
    return typeof payload.exp === "number" && payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
};

// Any authenticated request that comes back 401 means the session expired or was
// revoked: drop the stale token and send the user to admin login instead of
// letting the call fail silently (e.g. a design-discount save that never saves).
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401 && localStorage.getItem("token")) {
      localStorage.removeItem("token");
      if (!window.location.hash.includes("/admin-login")) {
        window.location.hash = "#/admin-login";
      }
    }
    return Promise.reject(error);
  }
);

const ProtectedRoute = ({ children }) => {
  if (isAdminAuthenticated()) return children;
  localStorage.removeItem("token");
  return <Navigate to="/admin-login" replace />;
};

const isChristmasSeason = () => {
  const today = new Date();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();

  // Check if it's between November 15 and December 31
  if (currentMonth === 11 && currentDay >= 15) return true;
  if (currentMonth === 12) return true;
  // Check if it's between January 1 and January 15
  if (currentMonth === 1 && currentDay <= 15) return true;

  return false;
};

root.render(
  <Provider store={store}>
    <PersistGate loading={null} persistor={persistor}>
      <LanguageProvider>
        <Router>
          <Routes>
            <Route path="/order-status/:id" element={<OrderStatus />} />
            <Route
              path="*"
              element={
                <>
                  {isChristmasSeason() && <SnowOverlay />}
                  <ScrollToTop>
                    <Header />
                    <Routes>
                      <Route path="/gallery" element={<Gallery />} />
                      <Route path="/gallery/:category" element={<Gallery />} />
                      <Route path="/blinds" element={<Blinds />} />
                      <Route
                        path="/cart"
                        element={isAdminAuthenticated() ? <AdminCart /> : <Cart />}
                      />
                      <Route path="/contact-us" element={<ContactUs />} />
                      <Route path="/item/:id" element={<Item />} />
                      <Route path="/admin-login" element={<Login />} />
                      <Route
                        path="/admin/calculator"
                        element={
                          <ProtectedRoute>
                            <Calculator />
                          </ProtectedRoute>
                        }
                      />
                      <Route
                        path="/admin/items"
                        element={
                          <ProtectedRoute>
                            <Items />
                          </ProtectedRoute>
                        }
                      />
                      <Route path="*" element={<Home />} />
                    </Routes>
                    <Footer />
                  </ScrollToTop>
                </>
              }
            />
          </Routes>
        </Router>
      </LanguageProvider>
    </PersistGate>
  </Provider>
);
